/**
 * Inline images are stored in rich text as an opaque reference rather than a URL.
 *
 * The URL that actually serves a file is environment-specific and cross-origin,
 * so persisting it would pin content written in one environment to that
 * environment's hosts forever. A custom scheme is used rather than a path
 * because it is obviously not fetchable — nothing downstream will try to load it
 * and silently render a broken image.
 */
export const FILE_REFERENCE_PREFIX = 'forecast-file:';

export const toFileReference = (fileId: string): string => `${FILE_REFERENCE_PREFIX}${fileId}`;

export const isFileReference = (value: string): boolean =>
	typeof value === 'string' && value.startsWith(FILE_REFERENCE_PREFIX) && value.length > FILE_REFERENCE_PREFIX.length;

/**
 * The markup a file chip is stored as: an inert span carrying the reference in a data
 * attribute rather than an href.
 *
 * Both halves of the converter match on these, so they live here as one spelling each.
 * A rename on one side alone leaves both suites green while every stored chip degrades
 * to plain text on its next save.
 */
/**
 * The class that marks a file chip in serialized HTML.
 *
 * The `slate-` prefix is load-bearing, not decoration. The editor exports rich text
 * through `elementToHtml`, which runs every element past `stripClassNames` — that keeps
 * only classes matching its `preserveClassNames` list, `['slate-']` by default. Under
 * the earlier `forecast-file-chip` the marker was deleted from the exported markup, so
 * the parse below no longer recognised the span on the way back in: the chip returned
 * as plain text, the reference was gone from the next save, and the file then read as
 * unreferenced. One save/reload cycle lost every file in a description.
 *
 * It must stay byte-identical to `CHIP_CLASS` in the design system's media serializer —
 * that constant is not exported from the package root, so this is a hand-kept copy and
 * the round-trip tests on each side are what hold the two together.
 */
export const FILE_CHIP_CLASS = 'slate-file-chip';
export const FILE_REF_ATTRIBUTE = 'data-file-ref';

/** Returns the file id, or null if this is not one of our references. */
export const fromFileReference = (reference: string): string | null =>
	isFileReference(reference) ? reference.slice(FILE_REFERENCE_PREFIX.length) : null;

/**
 * The label a chip falls back to when the file has no name.
 *
 * Both halves of the converter use it, and they have to agree: entities are attached
 * per character on the way in, so a chip with an empty label has no character to carry
 * its reference and the reference is lost with it. One spelling, so a chip written by
 * the emit side is always one the parse side can anchor.
 */
export const FILE_CHIP_FALLBACK_LABEL = 'File';

/**
 * The `data-name` / `data-size` / `data-mime` triplet every inline-media element carries,
 * read back off a parsed node.
 *
 * One function rather than a copy per element, because a player degrades to a chip and
 * the two must agree on what an unknown size looks like — the emit side has a single
 * writer for the same reason.
 *
 * `size` is OMITTED when the attribute is absent or is not a plain non-negative integer,
 * never coerced. `Number(null)` and `Number('')` are both 0, so coercing turns "size
 * unknown" into an assertion that a real file is 0 B — and because the emit side then
 * writes `data-size="0"`, which parses back as a legitimate zero, no side can ever tell
 * that apart from a genuinely empty file again. A chip restored from a Jira marker
 * carries the name only, so this is a live path, not a theoretical one.
 *
 * Matched as digits rather than handed to `Number`, which accepts every numeric literal
 * form JS has: `0x10` would become 16, `1e3` 1000, `12.5` a fractional byte count and
 * `-0` a value that passes `>= 0`. The emit side then rewrites whatever came out in
 * canonical decimal, so a fabricated count is indistinguishable from a real one after
 * one lap — the same unrecoverable state omitting the attribute exists to avoid.
 *
 * This is stricter than the design system's serializer, which gates the attribute on
 * `Number.isFinite(size) && size >= 0` and so can write a fractional byte count. Such a
 * value is dropped here and then absent from the next emit, which removes it from the
 * design system's copy too. Nothing produces one today — a real `File.size` is an
 * integer — and converging on the stricter gate is the fix if anything ever does.
 */
export const parsedMediaMetadata = (node: Element): {name: string; size?: number; mimeType: string} => {
	const rawSize = node.getAttribute('data-size')?.trim();
	const size = rawSize && /^\d+$/.test(rawSize) ? Number(rawSize) : NaN;

	return {
		name: node.getAttribute('data-name') || '',
		...(Number.isSafeInteger(size) ? {size} : {}),
		mimeType: node.getAttribute('data-mime') || '',
	};
};
